import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    AtSign, Eye, Gavel, HandCoins, MapPin, ScrollText, ShieldCheck,
    Timer, TrendingUp, Users, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { StatsStatusBadge } from "@/components/MarketCard";
import { SampleBanner } from "@/components/SampleBanner";
import { fetchMarketListing, fetchMarketWallet, placeMarketBid } from "@/lib/api";
import { money } from "@/lib/format";
import { compact, longDate, readWallet, statusMeta, timeLeft, watchTime } from "@/lib/market";

const BID_STATUS = {
    active: { label: "Leading", className: "bg-brand/15 text-brand border-brand/30" },
    outbid: { label: "Released", className: "bg-secondary text-muted-foreground" },
    won: { label: "Won", className: "bg-heat/15 text-heat border-heat/40" },
    lost: { label: "Lost", className: "bg-secondary text-muted-foreground" },
};

/** Auction room: stats first, then the bid box — proof rules always visible. */
export default function Auction() {
    const { id = "" } = useParams();
    const queryClient = useQueryClient();
    const [wallet] = useState(() => readWallet());
    const [amount, setAmount] = useState("");
    const [bidError, setBidError] = useState("");
    const [busy, setBusy] = useState(false);

    const listing = useQuery({
        queryKey: ["market-listing", id],
        queryFn: () => fetchMarketListing(id, wallet),
        refetchInterval: 15000,
    });
    const snap = useQuery({
        queryKey: ["market-wallet", wallet?.id],
        queryFn: () => fetchMarketWallet(wallet.id, wallet),
        enabled: !!wallet,
        refetchInterval: 20000,
    });

    const l = listing.data?.listing;
    const bids = listing.data?.bids || [];
    const deal = listing.data?.deal || null;
    const rules = listing.data?.rules || {};
    const w = snap.data?.wallet;

    if (listing.isLoading) {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-4">
                <Skeleton className="h-24 w-full rounded-2xl" />
                <div className="grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8 space-y-4">
                        <Skeleton className="h-40 rounded-2xl" />
                        <Skeleton className="h-32 rounded-2xl" />
                        <Skeleton className="h-48 rounded-2xl" />
                    </div>
                    <Skeleton className="h-64 rounded-2xl lg:col-span-4" />
                </div>
            </div>
        );
    }

    if (listing.isError || !l) {
        return (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
                <h1 className="font-heading text-2xl font-bold">Auction not found</h1>
                <p className="text-muted-foreground mt-2 mb-6">This listing may have been removed.</p>
                <Button asChild className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold">
                    <Link to="/market">Back to the market</Link>
                </Button>
            </div>
        );
    }

    const live = l.status === "live";
    const status = statusMeta(l.status);

    const place = async () => {
        const value = Math.trunc(Number(amount));
        if (!wallet) {
            toast.error("Set up a wallet first — bids hold real wallet funds.");
            return;
        }
        if (!Number.isFinite(value) || value <= 0) {
            setBidError("Enter a whole dollar amount");
            return;
        }
        if (value < l.next_bid_usd) {
            setBidError(
                `Minimum bid is ${money(l.next_bid_usd)} — current ${money(l.current_bid_usd)} + increment ${money(l.min_increment_usd)}`,
            );
            return;
        }
        if (w && value > w.available_usd) {
            setBidError(
                `Wallet short: ${money(w.available_usd)} available, ${money(value)} needed. Top up first.`,
            );
            return;
        }
        setBusy(true);
        setBidError("");
        try {
            await placeMarketBid(id, { wallet_id: wallet.id, amount_usd: value }, wallet);
            toast.success(`Bid placed — ${money(value)} held, nothing charged yet`);
            queryClient.invalidateQueries({ queryKey: ["market-listing", id] });
            queryClient.invalidateQueries({ queryKey: ["market-wallet", wallet.id] });
            setAmount("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bid failed — try again");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <SampleBanner className="mb-6" />

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <Badge variant="secondary">{l.slot_label}</Badge>
                        <Badge variant="secondary">{l.niche}</Badge>
                        <Badge
                            className={live
                                ? "bg-brand/15 text-brand border-brand/30 hover:bg-brand/15"
                                : "bg-secondary text-muted-foreground"}
                        >
                            {status.label}
                        </Badge>
                    </div>
                    <h1 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight">{l.title}</h1>
                    {l.event_name ? (
                        <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1.5">
                            <MapPin className="h-4 w-4" /> {l.event_name}
                            {l.event_date ? ` · ${longDate(l.event_date)}` : ""}
                        </p>
                    ) : null}
                </div>
                <div className="shrink-0 rounded-2xl border border-border bg-card px-5 py-4 text-right">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {l.has_bids ? `Current bid · ${l.bid_count} bid${l.bid_count === 1 ? "" : "s"}` : "Opening bid"}
                    </div>
                    <div className="font-heading text-3xl font-bold tabular">{money(l.current_bid_usd)}</div>
                    {l.has_bids && l.top_sponsor ? (
                        <div className="text-xs text-muted-foreground">{l.top_sponsor} leads</div>
                    ) : null}
                    <div className="mt-1 text-sm font-semibold tabular inline-flex items-center gap-1.5 text-heat">
                        <Timer className="h-4 w-4" /> {timeLeft(l.seconds_left)}
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8 space-y-6">
                    {/* 1 — creator metrics */}
                    <Card className="bg-card border-border p-5" data-testid="auction-metrics">
                        <div className="flex items-center justify-between gap-2 mb-4">
                            <h2 className="font-heading text-lg font-bold">Creator metrics</h2>
                            <StatsStatusBadge status={l.stats_status} />
                        </div>
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                            <Link
                                to={`/c/${l.creator_handle}`}
                                className="font-medium hover:text-brand transition-colors inline-flex items-center gap-1"
                            >
                                {l.creator_name} <AtSign className="h-3 w-3 opacity-60" />
                            </Link>
                            {l.city ? <span className="text-xs text-muted-foreground">{l.city}</span> : null}
                            <span className="text-xs text-muted-foreground">· next event {longDate(l.next_event_date)}</span>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="rounded-lg bg-surface-2 px-3 py-2.5">
                                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><Users className="h-3 w-3" /> Followers</div>
                                <div className="font-heading font-semibold tabular">{compact(l.followers)}</div>
                            </div>
                            <div className="rounded-lg bg-surface-2 px-3 py-2.5">
                                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><Eye className="h-3 w-3" /> Avg views 30d</div>
                                <div className="font-heading font-semibold tabular">{compact(l.avg_views_30d)}</div>
                            </div>
                            <div className="rounded-lg bg-surface-2 px-3 py-2.5">
                                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><Timer className="h-3 w-3" /> Watch time</div>
                                <div className="font-heading font-semibold tabular">{watchTime(l.avg_watch_time_sec)}</div>
                            </div>
                            <div className="rounded-lg bg-surface-2 px-3 py-2.5">
                                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><TrendingUp className="h-3 w-3" /> Engagement</div>
                                <div className="font-heading font-semibold tabular">{(l.engagement_rate * 100).toFixed(1)}%</div>
                            </div>
                        </div>
                        {l.sport_result ? (
                            <p className="text-xs text-muted-foreground mt-3">Result on record: {l.sport_result}</p>
                        ) : null}
                        <p className="text-[11px] text-muted-foreground mt-2">
                            {l.stats_status === "verified" ? "Verified by outbid.fit." : "Creator-reported (claimed) until verified."}
                        </p>
                    </Card>

                    {/* 2 — sponsor fit */}
                    <Card className="bg-card border-border p-5" data-testid="auction-fit">
                        <h2 className="font-heading text-lg font-bold mb-3">Why this fits sponsors</h2>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                            <Badge variant="secondary">{l.niche}</Badge>
                            {l.platform_list.map((p) => (
                                <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>
                            ))}
                            {l.event_name ? <Badge variant="outline" className="text-[10px]">{l.event_name}</Badge> : null}
                        </div>
                        {l.sponsor_fits.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No registered sponsor targets this niche yet.</p>
                        ) : (
                            <div className="space-y-2">
                                {l.sponsor_fits.map((s) => (
                                    <div key={s.sponsor_id} className="flex items-center justify-between gap-2 text-sm">
                                        <span className="font-medium">{s.brand}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {s.category} · min {compact(s.min_avg_views)} views · budget {compact(s.budget_usd)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    {/* 3 — deliverables */}
                    <Card className="bg-card border-border p-5" data-testid="auction-deliverables">
                        <h2 className="font-heading text-lg font-bold mb-3 inline-flex items-center gap-2">
                            <ScrollText className="h-5 w-5 text-brand" /> Deliverables
                        </h2>
                        <ul className="space-y-2">
                            {l.deliverables.length === 0 ? (
                                <li className="text-sm text-muted-foreground">Deliverables are described in the listing title.</li>
                            ) : l.deliverables.map((d, i) => (
                                <li key={i} className="text-sm flex gap-2">
                                    <ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> {d}
                                </li>
                            ))}
                        </ul>
                    </Card>

                    {/* 4 — bid box + wallet balance */}
                    <Card className="bg-card border-border p-5 glow-brand" data-testid="auction-bidbox">
                        <h2 className="font-heading text-lg font-bold mb-3 inline-flex items-center gap-2">
                            <Gavel className="h-5 w-5 text-brand" /> Place a bid
                        </h2>
                        {w ? (
                            <div className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2.5 mb-4 text-sm" data-testid="wallet-balance">
                                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                    <Wallet className="h-4 w-4" /> {w.label}
                                </span>
                                <span className="tabular">
                                    <strong>{money(w.available_usd)}</strong>
                                    <span className="text-muted-foreground"> available</span>
                                    {w.held_usd > 0 ? (
                                        <span className="text-muted-foreground"> · {money(w.held_usd)} held</span>
                                    ) : null}
                                </span>
                            </div>
                        ) : (
                            <div className="rounded-lg border border-dashed border-border px-3 py-2.5 mb-4 text-sm text-muted-foreground flex items-center justify-between gap-2">
                                No wallet on this device yet — create one to bid.
                                <Button asChild size="sm" variant="outline" className="font-semibold shrink-0">
                                    <Link to="/wallet">Open wallet</Link>
                                </Button>
                            </div>
                        )}

                        {live ? (
                            <>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <Input
                                        type="number"
                                        min={l.next_bid_usd}
                                        step={1}
                                        data-testid="bid-amount-input"
                                        value={amount}
                                        onChange={(e) => { setAmount(e.target.value); setBidError(""); }}
                                        placeholder={`${l.next_bid_usd} or more`}
                                        className="sm:max-w-[200px]"
                                    />
                                    <Button
                                        data-testid="bid-submit-button"
                                        onClick={place}
                                        disabled={busy || !wallet}
                                        className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                                    >
                                        {busy ? "Placing…" : `Bid ${amount ? money(Math.trunc(Number(amount) || 0)) : "now"}`}
                                    </Button>
                                    <div className="text-xs text-muted-foreground self-center sm:ml-2">
                                        Next min {money(l.next_bid_usd)} · increment {money(l.min_increment_usd)}
                                    </div>
                                </div>
                                {bidError ? (
                                    <div className="mt-3 rounded-lg border border-heat/40 bg-heat/10 px-3 py-2 text-sm text-heat flex items-center justify-between gap-3" data-testid="bid-error">
                                        <span>{bidError}</span>
                                        <Button asChild size="sm" variant="outline" className="border-heat/50 text-heat hover:bg-heat hover:text-primary-foreground font-semibold shrink-0">
                                            <Link to="/wallet">Top up</Link>
                                        </Button>
                                    </div>
                                ) : null}
                                <p className="text-[11px] text-muted-foreground mt-3">
                                    A bid moves funds from available to held. Outbid → released instantly.
                                    Win → captured only at close.
                                </p>
                            </>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                This auction has ended{deal ? " — see proof & payout status below" : ""}.
                            </p>
                        )}
                    </Card>

                    {/* 5 — bid history */}
                    <Card className="bg-card border-border p-5" data-testid="auction-history">
                        <h2 className="font-heading text-lg font-bold mb-3">Bid history</h2>
                        {bids.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No bids yet — the opening price stands.</p>
                        ) : (
                            <div className="divide-y divide-border">
                                {bids.map((b) => {
                                    const meta = BID_STATUS[b.status] || { label: b.status, className: "bg-secondary text-muted-foreground" };
                                    return (
                                        <div key={b.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                                            <span className="font-medium truncate">
                                                {b.sponsor_name}
                                                {b.is_mine ? <span className="text-brand"> · you</span> : null}
                                            </span>
                                            <span className="flex items-center gap-3 shrink-0">
                                                <Badge variant="outline" className={`text-[10px] ${meta.className}`}>{meta.label}</Badge>
                                                <span className="font-heading font-semibold tabular">{money(b.amount_usd)}</span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card>

                    {/* 6 — proof rules */}
                    <Card className="bg-card border-border p-5" data-testid="auction-proof-rules">
                        <h2 className="font-heading text-lg font-bold mb-3 inline-flex items-center gap-2">
                            <HandCoins className="h-5 w-5 text-brand" /> Proof & payout rules
                        </h2>
                        <ul className="space-y-2 text-sm">
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> The winner pays only when the auction closes — everyone else's hold releases immediately.</li>
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Creator has 7 days after close to upload proof: {l.proof_required || "photos and links of the delivered work"}.</li>
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Sponsor gets 48 hours to accept or dispute. Acceptance pays the creator the amount minus the 12% platform fee.</li>
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> No proof in 7 days → the winner's full amount returns to their wallet automatically.</li>
                        </ul>
                        {deal ? (
                            <Button asChild variant="outline" className="mt-4 font-semibold" data-testid="proof-link">
                                <Link to={`/proof/${deal.id}`}>Open proof room — {deal.status.replace(/_/g, " ")}</Link>
                            </Button>
                        ) : null}
                    </Card>
                </div>

                <aside className="lg:col-span-4 space-y-6">
                    <Card className="bg-card border-border p-4">
                        <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Auction facts</h3>
                        <dl className="space-y-2 text-sm">
                            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Starting bid</dt><dd className="tabular">{money(l.starting_bid_usd)}</dd></div>
                            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Min increment</dt><dd className="tabular">{money(l.min_increment_usd)}</dd></div>
                            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Next minimum</dt><dd className="tabular font-semibold">{money(l.next_bid_usd)}</dd></div>
                            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Slot</dt><dd>{l.slot_label}</dd></div>
                            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Event</dt><dd className="text-right">{l.event_name || "—"}</dd></div>
                            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Ends</dt><dd className="tabular text-right">{longDate(l.ends_at)}</dd></div>
                        </dl>
                    </Card>
                    <Card className="bg-card border-border p-4">
                        <h3 className="text-sm font-semibold mb-2">Platform fee</h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            12% of the final price, taken from the creator's payout at acceptance —
                            sponsors never pay a fee on top of their bid.
                        </p>
                        <Button asChild variant="link" className="px-0 mt-1 text-brand text-xs">
                            <Link to="/rules">Full market rules →</Link>
                        </Button>
                    </Card>
                </aside>
            </div>
        </div>
    );
}




